import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
} from '@/app/redux/endpoints/groups';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useGroups } from '@/app/redux/hooks/groups';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
  setStudentCreationCallback,
  setStudentCreationModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { Group } from '@/common/types/group';
import {
  DashboardViewActions,
  DashboardViewContainer,
  DashboardViewContent,
  DashboardViewHeader,
  DashboardViewTitle,
} from '@/common/ui/DashboardViewStyles';
import SearchInput from '@/common/ui/SearchInput';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  PlusIcon,
  UsersFourIcon,
} from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Input } from '@shadcn/components/input';
import { Label } from '@shadcn/components/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { AlertCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import styled from 'styled-components';
import { LearnerMultiSelect } from '../MultiSelect';
import GroupTableRow from './GroupTableRow';

export default function GroupsTable() {
  const dispatch = useAppDispatch();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { students } = useStudents();
  const { groups } = useGroups();

  const userId = useAppSelector(selectUserId);

  const [createGroupMutation] = useCreateGroupMutation();
  const [deleteGroupMutation] = useDeleteGroupMutation();

  const [groupsAsc, setGroupsAsc] = useState<boolean | null>(null);
  const [sortedGroups, setSortedGroups] = useState(groups || []);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState<string | JSX.Element>('');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(
    new Set(),
  );
  const [temporarilyHidden, setTemporarilyHidden] = useState(false);

  const handleAddItem = () => {
    setTemporarilyHidden(true);
    setStudentCreationCallback((studentId) => {
      setTemporarilyHidden(false);
      if (studentId) {
        setSelectedStudentIds((prev) => new Set([...prev, studentId]));
      }
    });
    dispatch(setStudentCreationModalOpen(true));
  };

  useEffect(() => {
    if (!searchTerm) {
      setFilteredGroups(sortedGroups);
      return;
    }

    const filtered = sortedGroups.filter((group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    setFilteredGroups(filtered);

    trackMixpanelEvent('Search Query Entered', {
      view: 'groups',
      searchTerm,
      resultsCount: filtered.length,
    });
  }, [searchTerm, sortedGroups, userId]);

  useEffect(() => {
    setError('');
  }, [groupModalOpen]);

  useEffect(() => {
    if (groupsAsc === null) setSortedGroups(groups);
    setSortedGroups(
      [...groups].sort((a, b) =>
        groupsAsc
          ? a.name.localeCompare(b.name, undefined, { numeric: true })
          : b.name.localeCompare(a.name, undefined, { numeric: true }),
      ),
    );
  }, [groups, groupsAsc]);

  async function handleCreateGroup() {
    if (!userId || !currentOrganisationId) {
      console.error('Missing user ID or org ID');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    if (!groupName) {
      setError('Please enter a name for the group');
      return;
    }

    try {
      const now = Date.now();
      const newGroup: Omit<Group, 'id'> = {
        name: groupName,
        organisationId: currentOrganisationId,
        managerIds: [userId],
        studentIds: Array.from(selectedStudentIds),
        createdAt: now,
        updatedAt: now,
      };
      await createGroupMutation(newGroup).unwrap();
      setGroupModalOpen(false);
      setGroupName('');
      setSelectedStudentIds(new Set());
      trackMixpanelEvent('Create Group', {
        groupName,
      });
      setError('');
      toast.success('Group created');
    } catch (error) {
      console.error(error);
      setError('Failed to create group');
    }
  }

  async function handleConfirmDeleteGroup(group: Group) {
    setConfirmationDialogueCallback(async () => {
      await handleDeleteGroup(group);
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Group',
        description: `Are you sure you want to delete the group "${group.name}"?`,
        variant: 'destructive',
      }),
    );
  }

  async function handleDeleteGroup(group: Group) {
    await deleteGroupMutation({
      orgId: currentOrganisationId!,
      groupId: group.id!,
    }).unwrap();
    toast.success('Group deleted');
    trackMixpanelEvent('Delete Group', {
      groupId: group.id,
      groupName: group.name,
    });
  }

  function handleSelectStudent(studentId: string) {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }

  function handleFilter(searchTerm: string) {
    return (students || []).filter((student) => {
      const key = `${student.studentFirstName} ${student.studentSurname} ${student.spellcastUsername}`;
      const terms = searchTerm.split(' ');
      return terms.every((term) =>
        key.toLowerCase().includes(term.toLowerCase()),
      );
    });
  }

  function getSortTooltipContent() {
    if (groupsAsc === null) {
      return 'Sort by name A to Z';
    }
    if (groupsAsc) {
      return 'Currently A to Z (click for Z to A)';
    }
    return 'Currently Z to A (click for A to Z)';
  }

  function handleSortChange() {
    const newSort = !groupsAsc;
    setGroupsAsc(newSort);

    trackMixpanelEvent('Sort Applied', {
      view: 'groups',
      column: 'name',
      direction: newSort ? 'asc' : 'desc',
    });
  }

  return (
    <DashboardViewContainer>
      <DashboardViewHeader>
        <DashboardViewTitle>
          Groups
          <Badge className="px-1 text-xxs" variant="secondary">
            {groups.length}
          </Badge>
        </DashboardViewTitle>
        <DashboardViewActions>
          <SearchInput
            placeholder="Search groups..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <Button
            variant="outline"
            className="flex gap-2"
            onClick={() => setGroupModalOpen(true)}
          >
            <PlusIcon size={20} />
            Add Group
          </Button>
        </DashboardViewActions>
      </DashboardViewHeader>

      <DashboardViewContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-4"></TableHead>
              <TableHead>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      onClick={handleSortChange}
                      className="hover:bg-transparent px-0"
                    >
                      Name
                      {groupsAsc === null ? (
                        <CaretUpDownIcon className="ml-2 h-4 w-4" />
                      ) : groupsAsc ? (
                        <CaretUpIcon className="ml-2 h-4 w-4" />
                      ) : (
                        <CaretDownIcon className="ml-2 h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{getSortTooltipContent()}</p>
                  </TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead>Learners</TableHead>
              <TableHead>Managers</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGroups.length ? (
              filteredGroups.map((group) => (
                <GroupTableRow
                  key={group.id}
                  group={group}
                  onConfirmDeleteGroup={() => handleConfirmDeleteGroup(group)}
                />
              ))
            ) : (
              <TableRow className="hover:bg-white">
                <TableCell colSpan={5}>
                  <div className="w-full min-h-[70vh] flex flex-col items-center justify-center py-12 gap-6">
                    <UsersFourIcon
                      size={80}
                      className="text-neutral-400"
                      weight="thin"
                    />
                    <div className="flex flex-col items-center gap-2">
                      <h3 className="text-lg font-medium text-neutral-900">
                        Create groups to organise your learners
                      </h3>
                      <p className="text-sm text-neutral-500 max-w-[400px] text-center">
                        Groups help you manage learners more efficiently. You
                        can create class groups, ability groups, or any other
                        organisation that suits your needs.
                      </p>
                    </div>
                    <Button onClick={() => setGroupModalOpen(true)}>
                      <PlusIcon size={20} className="mr-2" />
                      Add Group
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Dialog
          open={groupModalOpen && !temporarilyHidden}
          onOpenChange={(open) => setGroupModalOpen(open)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Group</DialogTitle>
              <DialogDescription>
                Add a new group to your organisation
              </DialogDescription>
            </DialogHeader>

            {error && (
              <Alert variant="destructive">
                <AlertCircle size={16} className="ml-2" />
                <span>{error}</span>
              </Alert>
            )}

            <InputContainer>
              <Label>Group Name</Label>
              <Input
                placeholder="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </InputContainer>

            <InputContainer>
              <Label>Learners</Label>
              <LearnerMultiSelect
                items={students}
                selectedIds={Array.from(selectedStudentIds)}
                onSelect={handleSelectStudent}
                onFilter={handleFilter}
                onAddItem={handleAddItem}
              />
            </InputContainer>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateGroup}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardViewContent>
    </DashboardViewContainer>
  );
}

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
